import { useEffect, useState } from 'react'
import { useContractInfo } from '@/hooks/useContractInfo'
import { Clock, TrendingDown, DollarSign } from 'lucide-react'
import { Card } from './ui/Card'

const AUCTION_INTERVAL = 6 * 60 * 60 // 6 hours in seconds

export const DutchAuctionTimer = () => {
  const { data: contractInfo, isLoading } = useContractInfo()
  const [timeLeft, setTimeLeft] = useState<number>(0)

  useEffect(() => {
    if (!contractInfo) return

    const calculateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000)
      const auctionStart = Number(contractInfo.auctionStartTime)
      const elapsed = now - auctionStart
      const intervalsPassed = Math.floor(elapsed / AUCTION_INTERVAL)
      const nextInterval = (intervalsPassed + 1) * AUCTION_INTERVAL
      const timeUntilNext = nextInterval - elapsed

      return timeUntilNext > 0 ? timeUntilNext : 0
    }

    // Initial calculation
    setTimeLeft(calculateTimeLeft())

    // Update every second
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [contractInfo])

  if (isLoading || !contractInfo) {
    return null
  }

  // Format time left
  const hours = Math.floor(timeLeft / 3600)
  const minutes = Math.floor((timeLeft % 3600) / 60)
  const seconds = timeLeft % 60

  // Check if price is at floor
  const isAtFloor = contractInfo.currentPriceFormatted === contractInfo.endPrice

  return (
    <Card className="mb-8 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Current Price */}
        <div className="text-center md:text-left">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="text-sm text-slate-400">Current Price</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {contractInfo.currentPriceFormatted} ETH/BNB
          </p>
        </div>

        {/* Price Range */}
        <div className="text-center md:text-left">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-5 h-5 text-blue-400" />
            <span className="text-sm text-slate-400">Price Range</span>
          </div>
          <p className="text-lg text-white">
            <span className="text-slate-400">{contractInfo.startPrice}</span>
            {' → '}
            <span className="text-blue-400">{contractInfo.endPrice} ETH/BNB</span>
          </p>
        </div>

        {/* Timer */}
        <div className="text-center md:text-left">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-slate-400">
              {isAtFloor ? 'At Floor Price' : 'Next Price Drop'}
            </span>
          </div>
          {isAtFloor ? (
            <p className="text-lg text-purple-400 font-medium">
              Minimum price reached!
            </p>
          ) : (
            <p className="text-2xl font-bold text-white font-mono">
              {String(hours).padStart(2, '0')}:
              {String(minutes).padStart(2, '0')}:
              {String(seconds).padStart(2, '0')}
            </p>
          )}
        </div>
      </div>

      {/* Info Banner */}
      {!isAtFloor && (
        <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <p className="text-sm text-purple-300 text-center">
            ⚡ Price decreases every 6 hours until reaching the floor price
          </p>
        </div>
      )}
    </Card>
  )
}
